/**
 * Discover Nearby Games Page Component
 * 
 * Allows users to discover and browse game templates created by other users
 * in their geographical area. Uses privacy-focused location sharing to find
 * relevant game templates while protecting user privacy.
 * 
 * Features:
 * - Location-based game template discovery
 * - Privacy-focused coordinate fuzzing
 * - Template usage tracking
 * - Game copying and customization
 * - Configurable search radius
 * - Fallback to browsing all templates
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { LocationPromptModal } from '../components/LocationPrompt';
import { LocationContexts, LocationService } from '../utils/locationServices';
import { useLocationStore } from '../stores/locationStore';

interface NearbyGame {
  id: number;
  name: string;
  totalTickets: number;
  ticketPrice: string;
  totalPayout: string;
  holdPercentage: string;
  templateUsageCount: number;
  distanceMeters: number;
  distanceFormatted: string;
  createdAt: string;
  approximateLocation: string;
  payoutStructure: {
    tickets: Array<{
      amount: string;
      quantity: number;
    }>;
  };
}

export default function DiscoverGames() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [nearbyGames, setNearbyGames] = useState<NearbyGame[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);
  const [searchRadius, setSearchRadius] = useState(25000); // 25km default
  // Only enable debug logging in development when DEBUG_LOCAL_GEOLOCATION is set to "true"
  const isDebugEnabled = import.meta.env.DEV && import.meta.env.VITE_DEBUG_LOCAL_GEOLOCATION === 'true';
  const [debugLog, setDebugLog] = useState<string[]>([]); // Debug overlay for mobile
  
  // Location services
  const locationPreferences = useLocationStore(state => state.preferences);
  const currentLocation = useLocationStore(state => state.currentLocation);
  const isLocationValid = useLocationStore(state => state.isLocationValid);
  const getFuzzedCoordinates = useLocationStore(state => state.getFuzzedCoordinates);
  const locationPermissionDenied = useLocationStore(state => state.locationPermissionDenied);
  const initializeLocation = useLocationStore(state => state.initialize);
  
  // Location actions (individual selectors instead of useLocationActions)
  const setCurrentLocation = useLocationStore(state => state.setCurrentLocation);
  const setLocationPermissionGranted = useLocationStore(state => state.setLocationPermissionGranted);
  const resetPermissionStates = useLocationStore(state => state.resetPermissionStates);

  useEffect(() => {
    initializeLocation();
  }, [initializeLocation]);

  const addDebugLog = (message: string, data?: any) => {
    // Only add debug logs if debugging is enabled
    if (!isDebugEnabled) return;
    
    const logEntry = data ? `${message} ${JSON.stringify(data)}` : message;
    // Only log to console in debug mode
    if (isDebugEnabled) {
      console.log('[GeoDebug]', logEntry);
    }
    setDebugLog(prev => [...prev.slice(-4), logEntry]); // Keep last 5 entries
  };

  const handleSearchNearby = async () => {
    // Check if we have valid location
    if (currentLocation && isLocationValid()) {
      await searchNearbyGames();
      return;
    }

    // Check if location was previously denied
    if (locationPermissionDenied) {
      setError('Location access was previously denied. Please enable location sharing in your browser settings or search all templates instead.');
      return;
    }

    // Show location prompt
    setShowLocationPrompt(true);
  };

  const searchNearbyGames = async () => {
    addDebugLog('🔍 searchNearbyGames called');
    
    // Get current store state for debugging
    const currentStore = useLocationStore.getState();
    addDebugLog('🏪 Current store state', {
      hasLocation: !!currentStore.currentLocation,
      permissionGranted: currentStore.locationPermissionGranted,
      permissionDenied: currentStore.locationPermissionDenied,
      isValid: currentStore.isLocationValid(),
      timestamp: currentStore.currentLocation?.timestamp || 'none'
    });
    
    const fuzzedCoords = getFuzzedCoordinates();
    addDebugLog('📍 getFuzzedCoordinates returned:', fuzzedCoords);
    
    if (!fuzzedCoords) {
      // More detailed debugging for why coordinates are null
      const debugStore = useLocationStore.getState();
      addDebugLog('❌ No fuzzed coordinates - detailed debug', {
        hasCurrentLocation: !!debugStore.currentLocation,
        locationValid: debugStore.isLocationValid(),
        currentTime: Date.now(),
        locationTimestamp: debugStore.currentLocation?.timestamp || 'none',
        timeDiff: debugStore.currentLocation ? Date.now() - debugStore.currentLocation.timestamp : 'N/A',
        fuzzedLocation: debugStore.currentLocation?.fuzzedLocation || 'none'
      });
      setError('Location access denied. Unable to get location coordinates for nearby search.');
      return;
    }

    console.log('⏳ Starting search with coords:', fuzzedCoords);
    setLoading(true);
    setError(null);

    try {
      const apiUrl = `/api/games/nearby?lat=${fuzzedCoords.latitude}&lng=${fuzzedCoords.longitude}&radius=${searchRadius}&limit=20`;
      console.log('🌐 Making API request to:', apiUrl);
      
      const response = await fetch(apiUrl);
      console.log('📡 API response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('📊 API response data:', data);
      const games = data.games || [];
      console.log('🎮 Games found:', games.length);
      setNearbyGames(games);
      
      // Don't set an error for empty results - let the empty state show instead
      // The user will see the "No games found" message in the empty state component
    } catch (err) {
      console.error('💥 Error searching nearby games:', err);
      setError('Failed to search nearby games. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLocationGranted = async (position: GeolocationPosition) => {
    addDebugLog('🎯 ENTER handleLocationGranted with position:', {
      lat: position.coords.latitude,
      lng: position.coords.longitude,
      accuracy: position.coords.accuracy
    });
    setShowLocationPrompt(false);
    
    // Store location in Zustand store using reactive actions
    const fuzzedLocation = LocationService.fuzzLocation({
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      accuracy: position.coords.accuracy,
    }, 1000);

    const userLocation = {
      coordinates: {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
      },
      fuzzedLocation,
      timestamp: Date.now(),
      context: 'TEMPLATE_DISCOVERY' as const,
    };

    addDebugLog('📦 Created userLocation object:', userLocation);

    // ✅ Use reactive store actions instead of getState()
    addDebugLog('🔧 Calling setCurrentLocation...');
    setCurrentLocation(userLocation);
    
    addDebugLog('🔧 Calling setLocationPermissionGranted...');
    setLocationPermissionGranted(true);
    
    // Use a setTimeout to check store state after React updates have been applied
    setTimeout(() => {
      const afterStore = useLocationStore.getState();
      addDebugLog('✅ After store update (delayed check):', {
        hasLocation: !!afterStore.currentLocation,
        permissionGranted: afterStore.locationPermissionGranted,
        timestamp: afterStore.currentLocation?.timestamp || 'none'
      });
    }, 100);
    
    addDebugLog('✅ Starting search...');
    await searchNearbyGames();
  };

  const handleLocationDenied = () => {
    setShowLocationPrompt(false);
    // Suggest browsing all templates instead
    setError('Location access denied. You can browse all templates without location filtering.');
  };

  const handleResetLocationPermissions = () => {
    // Clear LocationService preferences
    LocationService.clearLocationPreferences();
    
    // Reset Zustand store state
    resetPermissionStates();
    
    // Also clear localStorage directly to ensure persistence is cleared
    localStorage.removeItem('tabpicker-location-store');
    
    setError(null);
    
    // Try to show the location prompt again
    setShowLocationPrompt(true);
  };

  const handleLocationFallback = () => {
    setShowLocationPrompt(false);
    navigate('/dashboard'); // Or redirect to browse all templates
  };

  const handleCopyTemplate = async (templateId: number) => {
    try {
      const response = await fetch('/api/games/nearby/copy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ templateId }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      // Navigate to edit/customize the copied template
      navigate(`/create`, { 
        state: { 
          templateData: data.template,
          mode: 'create_from_template' 
        } 
      });
    } catch (err) {
      console.error('Error copying template:', err);
      setError('Failed to copy template. Please try again.');
    }
  };

  const formatPrice = (price: string | undefined | null) => {
    if (!price) return 'N/A';
    const num = parseFloat(price);
    if (isNaN(num)) return 'N/A';
    return num.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
  };

  const formatPercentage = (pct: string | undefined | null) => {
    if (!pct) return 'N/A';
    const num = parseFloat(pct);
    if (isNaN(num)) return 'N/A';
    return `${num.toFixed(1)}%`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-2 tablet:px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="text-blue-600 hover:text-blue-800 font-medium"
              >
                ← Back to Dashboard
              </button>
              <h1 className="text-2xl font-bold text-gray-900">Discover Nearby Games</h1>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-gray-600">{user?.name}</span>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-2 tablet:px-4 py-8">
        {/* Debug Panel - Only show in development with DEBUG_LOCAL_GEOLOCATION=true */}
        {isDebugEnabled && debugLog.length > 0 && (
          <div className="bg-black text-green-400 p-4 rounded-lg font-mono text-sm tablet:text-xs mb-4">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-white font-bold">🔧 Debug Log (Development Only)</h3>
              <button 
                onClick={() => setDebugLog([])}
                className="text-red-400 hover:text-red-300"
              >
                Clear
              </button>
            </div>
            {debugLog.map((log, index) => (
              <div key={index} className="mb-1">{log}</div>
            ))}
          </div>
        )}

        {/* Search Controls */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="flex flex-col tablet:flex-row gap-4 items-start tablet:items-center justify-between">
            <div className="flex-1">
              <h2 className="text-xl tablet:text-lg font-semibold text-gray-900 mb-2">
                Find Game Templates Near You
              </h2>
              <p className="text-gray-600 text-base tablet:text-sm">
                Discover pull tab game templates created by other users in your area. 
                Your location is fuzzed for privacy protection.
              </p>
            </div>
            
            <div className="flex gap-3 items-center">
              <div className="flex items-center gap-2">
                <label htmlFor="radius" className="text-base tablet:text-sm font-medium text-gray-700">
                  Search Radius:
                </label>
                <select
                  id="radius"
                  value={searchRadius}
                  onChange={(e) => setSearchRadius(Number(e.target.value))}
                  className="px-3 py-1 border border-gray-300 rounded-md text-base tablet:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={5000}>5km</option>
                  <option value={10000}>10km</option>
                  <option value={25000}>25km</option>
                  <option value={50000}>50km</option>
                  <option value={100000}>100km</option>
                </select>
              </div>
              
              <button
                onClick={handleSearchNearby}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Searching...' : 'Search Nearby'}
              </button>
              
              {/* Debug button - Only show in development with DEBUG_LOCAL_GEOLOCATION=true */}
              {isDebugEnabled && (
                <button
                  onClick={() => {
                    const store = useLocationStore.getState();
                    const coords = store.getFuzzedCoordinates();
                    alert(`Store Debug:\nHas Location: ${!!store.currentLocation}\nPermission Granted: ${store.locationPermissionGranted}\nFuzzed Coords: ${coords ? `${coords.latitude}, ${coords.longitude}` : 'null'}\nPermission Denied: ${store.locationPermissionDenied}`);
                  }}
                  className="px-3 py-2 bg-gray-600 text-white rounded-md text-base tablet:text-sm"
                >
                  🔍 Debug
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <p className="text-red-700">{error}</p>
              </div>
              <button
                onClick={handleResetLocationPermissions}
                className="ml-4 px-3 py-1 bg-blue-600 text-white text-base tablet:text-sm rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Try Again
              </button>
            </div>
          </div>
        )}

        {/* Results */}
        {nearbyGames.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-xl tablet:text-lg font-semibold text-gray-900">
                Found {nearbyGames.length} game templates
              </h3>
              <p className="text-sm text-gray-600">
                Within {searchRadius >= 1000 ? `${searchRadius/1000}km` : `${searchRadius}m`} of your location
              </p>
            </div>

            {nearbyGames.map((game) => (
              <div key={game.id} className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow">
                <div className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h4 className="text-2xl tablet:text-xl font-semibold text-gray-900 mb-1">
                        {game.name}
                      </h4>
                      <p className="text-sm text-gray-600">
                        {game.approximateLocation} • Used {game.templateUsageCount} times
                      </p>
                    </div>
                    <button
                      onClick={() => handleCopyTemplate(game.id)}
                      className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                    >
                      Copy Template
                    </button>
                  </div>

                  <div className="grid grid-cols-2 tablet:grid-cols-4 gap-4 mb-4">
                    <div>
                      <div className="text-base tablet:text-sm text-gray-600">Ticket Price</div>
                      <div className="font-semibold">{formatPrice(game.ticketPrice)}</div>
                    </div>
                    <div>
                      <div className="text-base tablet:text-sm text-gray-600">Total Tickets</div>
                      <div className="font-semibold">{game.totalTickets?.toLocaleString() || 'N/A'}</div>
                    </div>
                    <div>
                      <div className="text-base tablet:text-sm text-gray-600">Total Payout</div>
                      <div className="font-semibold">{formatPrice(game.totalPayout)}</div>
                    </div>
                    <div>
                      <div className="text-base tablet:text-sm text-gray-600">House Hold</div>
                      <div className="font-semibold">{formatPercentage(game.holdPercentage)}</div>
                    </div>
                  </div>

                  {game.payoutStructure?.tickets?.length > 0 && (
                    <div>
                      <div className="text-base tablet:text-sm text-gray-600 mb-2">Prize Structure</div>
                      <div className="flex flex-wrap gap-2">
                        {game.payoutStructure.tickets.slice(0, 5).map((ticket, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm tablet:text-xs font-medium bg-blue-100 text-blue-800"
                          >
                            {ticket.quantity}× {formatPrice(ticket.amount)}
                          </span>
                        ))}
                        {game.payoutStructure.tickets.length > 5 && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm tablet:text-xs font-medium bg-gray-100 text-gray-800">
                            +{game.payoutStructure.tickets.length - 5} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!loading && nearbyGames.length === 0 && !error && (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <h3 className="mt-2 text-base tablet:text-sm font-medium text-gray-900">
              {currentLocation && isLocationValid() ? 'No nearby games found' : 'Search for nearby games'}
            </h3>
            <p className="mt-1 text-base tablet:text-sm text-gray-500">
              {currentLocation && isLocationValid() ? 
                `No game templates found within ${searchRadius >= 1000 ? `${searchRadius/1000}km` : `${searchRadius}m`} of your location. Try expanding your search radius or check back later.` :
                'Click "Search Nearby" to discover game templates from other TabPicker users in your area.'
              }
            </p>
          </div>
        )}
      </main>

      {/* Location Prompt Modal */}
      <LocationPromptModal
        isOpen={showLocationPrompt}
        onClose={() => setShowLocationPrompt(false)}
        context={LocationContexts.TEMPLATE_DISCOVERY}
        contextType="TEMPLATE_DISCOVERY"
        onLocationGranted={handleLocationGranted}
        onLocationDenied={handleLocationDenied}
        onFallback={handleLocationFallback}
      />
    </div>
  );
}